import { getAvailableGiveawaysTotalStock } from '@cart-services/core/couponService'
import { type MergedCoupon } from '@cart-services/types'
import { type CountryCode } from '@constants'
import { AppliedStatus, NewOrExistingCustomerRestriction, UsageMethod } from '@hooks-api/types/dataModel/coupon'
import { findLastIndex } from '@utils/findLastIndex'
import { type ForwardedRef, useImperativeHandle, useRef } from 'react'
import { CouponCondition } from '../CouponCondition/CouponCondition'
import { CouponConditionListGrid } from '../CouponConditionList/CouponConditionListGrid'
import { CouponListItem, type CouponListItemHandler } from '../CouponListItem/CouponListItem'
import { CouponListSkeletons } from '../CouponListSkeletons/CouponListSkeletons'

export interface CouponListProps {
  coupons?: MergedCoupon[]
  countryCode?: CountryCode
  currency?: string
  onManualCouponDelete?: (coupon: MergedCoupon) => void
  selectedManualCouponId?: number | null
  onManualCouponSelectionChange?: (couponId: number) => void
  /**
   * 唯讀模式：選取/刪除控制 disabled（結帳頁僅能檢視不可編輯）。
   */
  isSelectionDisabled?: boolean
  onGoShopping?: (coupon: MergedCoupon) => void
  listItemRefs?: ForwardedRef<Record<number, CouponListItemHandler | null>>
  goShoppingHref?: (coupon: MergedCoupon) => string
  isGoShoppingDisabled?: boolean
  isUserLoggedIn?: boolean
}

export const CouponList = (props: CouponListProps) => {
  const { coupons, countryCode, currency, onManualCouponDelete, selectedManualCouponId, onManualCouponSelectionChange, isSelectionDisabled, onGoShopping, listItemRefs, goShoppingHref, isGoShoppingDisabled, isUserLoggedIn } = props
  const innerListItemRefs = useRef<Record<number, CouponListItemHandler | null>>({})
  useImperativeHandle(listItemRefs, () => {
    return innerListItemRefs.current
  })

  const handleManualCouponDelete = (coupon: MergedCoupon) => onManualCouponDelete ? () => onManualCouponDelete(coupon) : undefined
  const handleManualCouponSelectionChange = (coupon: MergedCoupon) => onManualCouponSelectionChange ? () => onManualCouponSelectionChange(coupon.coupon_id) : undefined
  const handleGoShopping = (coupon: MergedCoupon) => onGoShopping ? () => onGoShopping(coupon) : undefined

  const renderCouponCondition = (availableCoupon: MergedCoupon) => {
    const lastCanAppliedCriterionIndex = findLastIndex(availableCoupon.criterions, criterion => criterion.applied_status === AppliedStatus.APPLIED)
    const lastCanAppliedCriterion = availableCoupon.criterions[Math.min(lastCanAppliedCriterionIndex + 1, availableCoupon.criterions.length - 1)]
    if (!lastCanAppliedCriterion) return null
    return (
      <CouponConditionListGrid>
        <CouponCondition
          isApplied={lastCanAppliedCriterion.applied_status === AppliedStatus.APPLIED}
          currency={currency}
          moreAmount={lastCanAppliedCriterion.left_amount}
          moreQuantity={lastCanAppliedCriterion.left_quantity ?? lastCanAppliedCriterion.display_criterion_quantity}
          count={lastCanAppliedCriterion.count}
          useCssGrid
          shouldComposedLabel
          coupon={availableCoupon}
        />
      </CouponConditionListGrid>
    )
  }

  if (coupons === undefined) return <CouponListSkeletons rows={5} />

  return coupons.map((availableCoupon) => {
    const isApplied = availableCoupon.criterions.some(criterion => criterion.applied_status === AppliedStatus.APPLIED)
    const giveaways = availableCoupon.criterions.flatMap(criterion => criterion.giveaways)
    /** 裡面的 `giveaways.length === 0 ? false` 這個判斷式是因為有些券如運費券是沒有 giveaways 的，不應當做庫存不足 */
    const isOutOfStock = giveaways.length === 0 ? false : getAvailableGiveawaysTotalStock(giveaways) === 0

    return (
      <CouponListItem
        key={availableCoupon.coupon_id}
        rootRef={(element) => {
          innerListItemRefs.current[availableCoupon.coupon_id] = element
        }}
        title={availableCoupon.display_name}
        isApplied={isApplied}
        isReachUserLimit={availableCoupon.is_over_user_limit}
        isRunOut={availableCoupon.is_over_ticket_limit}
        isOutOfStock={isOutOfStock}
        isNewCustomerOnly={availableCoupon.new_or_existing_customer_restriction === NewOrExistingCustomerRestriction.NEW}
        isOldCustomerOnly={availableCoupon.new_or_existing_customer_restriction === NewOrExistingCustomerRestriction.OLD}
        startTime={availableCoupon.start_time}
        endTime={availableCoupon.end_time}
        currentUsageCount={availableCoupon.usage_count}
        countryCode={countryCode}
        maxUsageLimit={availableCoupon.usage_limit}
        isManualInput={availableCoupon.usage_method === UsageMethod.MANUAL}
        criterionGroups={availableCoupon.criterion_groups}
        couponCode={availableCoupon.usage_method === UsageMethod.MANUAL ? (availableCoupon.coupon_code || '') : undefined}
        thumbnailUrl={availableCoupon.thumbnail_url}
        onManualCouponDelete={handleManualCouponDelete(availableCoupon)}
        isManualCouponSelected={selectedManualCouponId === availableCoupon.coupon_id}
        onManualCouponSelectionChange={handleManualCouponSelectionChange(availableCoupon)}
        isSelectionDisabled={isSelectionDisabled}
        onGoShopping={handleGoShopping(availableCoupon)}
        goShoppingHref={goShoppingHref?.(availableCoupon)}
        isGoShoppingDisabled={isGoShoppingDisabled}
        isUserLoggedIn={isUserLoggedIn}
      >
        {renderCouponCondition(availableCoupon)}
      </CouponListItem>
    )
  })
}
